<template>
    <div :class="`${getClassName('wrapper')} autocomplete-wrapper`" data-testid="autocomplete_wrapper">
        <div class="backdrop" @click="showList = false" v-if="showList"></div>
        <div :class="{ 'is-active': isActive }" class="dropdown">
            <div class="dropdown-trigger">
                <label :class="`${getClassName('label')} label`" v-if="label" data-testid="label">
                    {{ label }}
                </label>
                <p class="control">
                    <template v-if="textareaField">
                        <textarea
                            ref="input"
                            :id="id"
                            :class="[
                                { 'resizable-textarea': resizable },
                                getClassName('input'),
                                { 'is-danger': hasError },
                            ]"
                            class="input autocomplete-input"
                            :placeholder="placeholder"
                            :name="name"
                            v-model="type"
                            @input="handleInput"
                            @dblclick="handleDoubleClick"
                            @blur="handleBlur"
                            @keydown="handleKeyDown"
                            @focus="handleFocus"
                            @keyup.ctrl.67="handleInput"
                            @change="$emit('change', $event)"
                            autocomplete="off"
                            :disabled="config['disabled'] ? config['disabled'] : false"
                            data-lpignore="true"
                            v-bind="$attrs"
                        />
                    </template>
                    <template v-else>
                        <div class="input-wrap">
                            <input
                                type="text"
                                ref="input"
                                :id="id"
                                :class="[
                                    getClassName('input'),
                                    { 'with-character-count': showCharacterCount },
                                    { 'is-danger': hasError },
                                ]"
                                class="input autocomplete-input"
                                :placeholder="placeholder"
                                :name="name"
                                v-model="type"
                                @input="handleInput"
                                @dblclick="handleDoubleClick"
                                @blur="handleBlur"
                                @keydown="handleKeyDown"
                                @focus="handleFocus"
                                @keyup.ctrl.67="handleInput"
                                @change="$emit('change', $event)"
                                autocomplete="off"
                                :disabled="config['disabled'] ? config['disabled'] : false"
                                data-lpignore="true"
                                data-testid="autocomplete_input"
                                v-bind="$attrs"
                            />
                            <div
                                v-if="showCharacterCount"
                                :class="['character-count', { 'over-limit': overCharacterLimit }]"
                            >
                                {{ characterCountDisplay }}
                            </div>
                        </div>
                    </template>
                    <template v-if="hasErrorArray">
                        <p class="help is-danger" v-for="(error, index) in errors[name]" :key="index">{{ error }}</p>
                    </template>
                    <template v-else-if="hasError">
                        <p class="help is-danger">{{ errors[name] }}</p>
                    </template>
                </p>
            </div>
            <div :class="`${getClassName('list')} dropdown-menu`" role="menu" v-show="showList">
                <div class="dropdown-content" ref="dropdownContent" :style="{ 'max-height': dropdownMaxHeight }">
                    <a
                        v-for="(data, i) in json"
                        :class="activeClass(i)"
                        class="dropdown-item"
                        href="#"
                        @click.prevent="selectList(data)"
                        @drag="selectList(data)"
                        @mousemove="mousemove(i)"
                        :key="i"
                        :data-testid="`dropdownItem${i}`"
                    >
                        <div v-if="onShouldRenderChild">{{ onShouldRenderChild(data) }}</div>
                        <div v-if="!onShouldRenderChild">
                            <b class="autocomplete-anchor-text">{{ deepValue(data, config.anchorFrom) }}</b>
                            <span
                                class="autocomplete-anchor-label"
                                v-if="config.labelFrom"
                                v-html="deepValue(data, config.labelFrom)"
                            ></span>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/*
     Autocomplete accepts next props
     config (Object)
     Consists of url, anchorFrom, labelFrom, param, customParams, process,
     Example of config
     {
     // url is required
     // url parameter can be Api object or url endpoint
     url: ProductApi,
     // anchorFrom is required
     // anchorFrom is String and it is used as nam
     anchorFrom: 'partNumber'  - String,
     // labelFrom is optional
     // labelFrom is String and it is used as description
     labelFrom: 'partNumber'  String,
     // param is required
     // param is String and it is main parameter which is used for search
     param: 'part_number',
     // customParams is optional
     // customParams is Object of custom parameters which will be passed to query, or we can include some of objects
     customParams: {
     category_id: 10,
     includes:[
     'manufacturer'
     ],
     },
     // process is optional
     // Function to process the API result with. Should return an array of entries or an object whose properties can be enumerated.
     // data - retrived array
     // value - searched keyword
     process: (data, val)=>{}
     },

     configTemplate (String)
     Name of the predefined templates for autocomplete
     (patientNameWithForStock,patientName, partNumDescriptionManualOrExtraOrPriceList, partNumDescription, partId, partNum, partNumFacilityBasedOrderHistory, partNumFacilityBasedInventory, PartNumFacilityShoppingList)

     min (Number: 0)
     Minimum input typed chars before performing the search query. default is 0

     filterByAnchor (Boolean: true)
     When you're using options props, you can have autocomplete to filter your data. Or you can just show your data directly without any filter from autocomplete. The options will be filtered by anchor and it according to the user input.

     encodeParams (Boolean: true)
     Autocomplete will encodeURIComponent all your params before ajax send, When this props sets to true. Default is true #35

     debounce (Number)
     Delay time before do the ajax for the data

     required (Boolean)
     Required attribute for input

     placeholder (String)
     Placeholder for input

     className (String)
     Custom class name for autocomplete component

     classes (Object)
     Spesific custom class for each part. available: wrapper, input, list, and item

     id (String)
     Custom id name for autocomplete component

     debounce (number)
     Number of milliseconds the user should stop typing for before the request is sent. Default is 0, meaning all requests are sent immediately.
     */

import _ from 'lodash';
import Base from 'api/endpoints/base';
import axios from 'axios';
import { hasErrors } from './mixins';
import { requestInterceptor as clientRequestInterceptor } from 'api/client';

const pipeP =
    (...fns) =>
    (args) =>
        fns.reduce((arg, fn) => arg.then(fn), Promise.resolve(args));

const BLANK_CONFIG = {
    anchorFrom: false,
    labelFrom: false,
    url: false,
    param: 'q',
    customParams: {},
    disabled: false,
};

/*!
     Lot of parts of this component is used from url, some of them are changed to fit our needs.

     https://github.com/BosNaufal/vue2-autocomplete

     Copyright (c) 2016 Naufal Rabbani (http://github.com/BosNaufal)
     * Licensed Under MIT (http://opensource.org/licenses/MIT)
     *
     * Vue 2 Autocomplete @ Version 0.0.1
     *
     */

export default {
    inheritAttrs: false,
    mixins: [hasErrors],
    props: {
        id: String,
        name: String,
        value: String,
        resizable: {
            type: Boolean,
            default: false,
        },
        className: String,
        label: String,
        classes: {
            type: Object,
            default: () => ({
                wrapper: false,
                input: false,
                list: false,
                item: false,
                label: false,
            }),
        },
        placeholder: String,
        required: Boolean,
        useJsd: {
            type: Boolean,
            default: false,
        },

        // Intial Value
        initValue: {
            type: [String, Number],
            default: '',
        },

        // Manual List
        options: Array,

        // Filter After Get the data
        filterByAnchor: {
            type: Boolean,
            default: true,
        },

        config: {
            type: Object,
            validator: (obj) => {
                return obj.anchorFrom && obj.url && obj.param;
            },
        },

        // Debounce time
        debounce: {
            type: Number,
            default: 500,
        },

        // minimum length
        min: {
            type: Number,
            default: 0,
        },

        textareaField: {
            type: Boolean,
            default: false,
        },

        // Create a custom template from data.
        onShouldRenderChild: Function,

        // Callback
        onInput: Function,
        onShow: Function,
        onBlur: Function,
        onHide: Function,
        onFocus: Function,
        onSelect: Function,
        onBeforeAjax: Function,
        onAjaxProgress: Function,
        onAjaxLoaded: Function,
        onShouldGetData: Function,
        dropdownMaxHeight: {
            type: String,
            default: '25vh',
        },

        showCharacterCount: {
            type: Boolean,
            default: false,
        },
        characterLimit: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            showList: false,
            type: '',
            json: [],
            focusList: '',
            debounceTask: undefined,
            requestId: 0,
            aborter: {
                controller: new AbortController(),
            },
        };
    },

    watch: {
        options(newVal, oldVal) {
            if (this.filterByAnchor) {
                const type = this.type;
                const anchorFrom = this.config.anchorFrom;

                const regex = new RegExp(`${type}`, 'i');
                const filtered = newVal.filter((item) => {
                    const found = item[anchorFrom].search(regex) !== -1;
                    return found;
                });
                this.json = filtered;
            } else {
                this.json = newVal;
            }
        },
        value(newVal, oldVal) {
            if (newVal !== this.type) {
                this.type = newVal;
            }
        },
    },

    methods: {
        getClassName(part) {
            const { classes, className } = this;
            if (classes[part]) return `${classes[part]}`;
            return className ? `${className}-${part}` : '';
        },

        // Netralize Autocomplete
        clearInput() {
            this.showList = false;
            this.type = '';
            this.json = [];
            this.focusList = '';
        },

        // Get the original data
        cleanUp(data) {
            try {
                return JSON.parse(JSON.stringify(data));
            } catch (e) {
                console.log('Error parsing JSON in Autocomplete.cleanUp()', {
                    data,
                    json: this.json,
                    query: this.type,
                });
                throw e;
            }
        },

        /*==============================
             INPUT EVENTS
             =============================*/
        handleInput(e) {
            let { value } = e.target;
            value = value.replace(/(\r\n|\n|\r)/gm, ' ');

            this.showList = false;
            // Callback Event
            if (this.onInput) this.onInput(value);
            // If Debounce
            if (this.debounce) {
                if (this.debounceTask !== undefined) clearTimeout(this.debounceTask);
                this.debounceTask = setTimeout(() => {
                    return this.getData(value);
                }, this.debounce);
            } else {
                return this.getData(value);
            }
            // emit value as input
            this.$emit('input', value);
            this.selectTopResult();
        },

        handleKeyDown(e) {
            let key = e.keyCode;

            // Disable when list isn't showing up
            if (!this.showList) return;

            // Key List
            const DOWN = 40;
            const UP = 38;
            const ENTER = 13;
            const ESC = 27;

            // Prevent Default for Prevent Cursor Move & Form Submit
            switch (key) {
                case DOWN:
                    e.preventDefault();
                    this.focusList++;
                    break;
                case UP:
                    e.preventDefault();
                    this.focusList--;
                    break;
                case ENTER:
                    e.preventDefault();
                    this.selectList(this.json[this.focusList]);
                    this.showList = false;
                    break;
                case ESC:
                    this.showList = false;
                    break;
            }

            const listLength = this.json.length - 1;
            const outOfRangeBottom = this.focusList > listLength;
            const outOfRangeTop = this.focusList < 0;
            const topItemIndex = 0;
            const bottomItemIndex = listLength;

            let nextFocusList = this.focusList;
            if (outOfRangeBottom) nextFocusList = topItemIndex;
            if (outOfRangeTop) nextFocusList = bottomItemIndex;
            this.focusList = nextFocusList;

            this.scrollIntoView();
        },

        selectTopResult() {
            this.focusList = 0;
            this.scrollIntoView();
        },

        setValue(val) {
            this.type = val;
        },

        scrollIntoView() {
            const currentSelectedListIndex = this.focusList;
            const currentListItems = this.$refs.dropdownContent.querySelectorAll('.dropdown-item');

            if (currentListItems.length) {
                const currentListItem = currentListItems[this.focusList];

                this.$refs.dropdownContent.scrollTop = currentListItem.offsetTop;
            }
        },

        /*==============================
             LIST EVENTS
             =============================*/

        handleDoubleClick() {
            this.json = [];
            this.getData('');
            // Callback Event
            this.onShow ? this.onShow() : null;
            this.showList = true;
        },

        handleBlur(e) {
            // Callback Event
            this.onBlur ? this.onBlur(e) : null;
            setTimeout(() => {
                // Callback Event
                this.onHide ? this.onHide() : null;
                this.showList = false;
            }, 250);
        },

        handleFocus(e) {
            this.focusList = 0;
            // Callback Event
            this.onFocus ? this.onFocus(e) : null;
        },

        mousemove(i) {
            this.focusList = i;
        },

        activeClass(i) {
            const focusClass = i === this.focusList ? 'is-active' : '';
            return `${focusClass}`;
        },

        selectList(data) {
            // Deep clone of the original object
            const clean = this.cleanUp(data);
            // Put the selected data to type (model)
            this.type = clean[this.config.anchorFrom];
            // Hide List
            this.showList = false;
            // Callback Event
            this.onSelect ? this.onSelect(clean) : null;

            // emit value as input
            this.$emit('input', this.type);
        },

        deepValue(obj, path) {
            const arrayPath = path.split('.');
            for (let i = 0; i < arrayPath.length; i++) {
                if (obj && arrayPath[i] in obj) {
                    obj = obj[arrayPath[i]];
                }
            }
            return this.sanitizeHTML(obj, { allowedTags: ['br'] });
        },

        /*==============================
             AJAX EVENTS
             =============================*/

        composeParams(val) {
            const encode = (val) => (this.encodeParams ? encodeURIComponent(val) : val);
            let params = {};
            params[this.config.param] = val;
            if (this.config.customParams) {
                params = _.merge({}, params, this.config.customParams);
            }
            return params;
        },

        async doAjax(val) {
            if (val) {
                // Callback Event
                this.onBeforeAjax ? this.onBeforeAjax(val) : null;
                this.requestId++;

                // Setup Abort Controller
                if (this.aborter.controller) {
                    this.aborter.controller.abort();
                }

                this.aborter.controller = new AbortController();

                // Compose Params
                let params = this.composeParams(val);
                let result;

                try {
                    switch (true) {
                        case this.useJsd:
                            result = await this.fetchWithJSD(params);
                            break;
                        case typeof this.config.url === 'function':
                            result = await this.fetchWithFunction(params);
                            break;
                        case typeof this.config.url === 'object':
                            result = await this.fetchWithObject(params);
                            break;
                        default:
                            result = await this.fetchWithAxios(params);
                            break;
                    }
                } catch (e) {
                    if (e.name === 'AbortError') {
                        return;
                    }
                    throw e;
                }

                this.updateJson(result, val);

                if (this.json.length) {
                    this.showList = true;
                }
            }
        },

        buildSwaggerRequestInterceptors() {
            const interfaceLevelRequestInterceptor = (req) => {
                req.signal = this.aborter.controller.signal;
                return req;
            };
            const autocompleteRequestInterceptor = (req) => {
                req.headers = Object.assign({}, req.headers, {
                    'x-autocomplete-request-id': this.requestId,
                });
                return req;
            };
            const requestInterceptor = pipeP(
                clientRequestInterceptor,
                pipeP(autocompleteRequestInterceptor, interfaceLevelRequestInterceptor)
            );
            return { requestInterceptor };
        },

        async fetchWithJSD(params) {
            const apiMethod = this.config.url;
            const swaggerOptions = this.buildSwaggerRequestInterceptors();
            return await apiMethod(params, { force: true, raw: true }, swaggerOptions);
        },

        async fetchWithFunction(params) {
            const apiMethod = this.config.url;
            const swaggerOptions = this.buildSwaggerRequestInterceptors();
            return await apiMethod(params, swaggerOptions); // Assume apiMethod is a swagger client function and send swaggerOptions
        },

        async fetchWithObject(params) {
            const apiMethod = this.config.url;
            return await apiMethod.get(params);
        },

        async fetchWithAxios(params) {
            let url = Base.getApiUrl(this.config.url);
            return await axios.get(url, {
                hideLoader: true,
                headers: {
                    'x-autocomplete-request-id': this.requestId,
                },
                signal: this.aborter.controller.signal,
                params,
            });
        },

        updateJson(response, val) {
            let autocomplete_request_id = _.get(response, 'response.headers.x-autocomplete-request-id');

            if (!autocomplete_request_id) {
                autocomplete_request_id = _.get(response, 'headers.x-autocomplete-request-id');
            }

            if (autocomplete_request_id && Number(autocomplete_request_id) !== this.requestId) {
                return false;
            }

            let responseData = this.getDataFromResponse(response);
            this.json = this.config.process ? this.config.process(responseData, val) : responseData;
        },

        getData(value) {
            value = (value ?? '').trim();
            if (value.length < this.min || !this.config.url) return;
            if (this.onShouldGetData) this.manualGetData(value);
            else this.doAjax(value);
        },

        getDataFromResponse(response) {
            if (this.useJsd) {
                if ('data' in response) {
                    return response.data;
                }
            } else if ('body' in response) {
                return response.body.data;
            }

            return response;
        },

        // Do Ajax Manually, so user can do whatever he want
        manualGetData(val) {
            const task = this.onShouldGetData(val);
            if (task && task.then) {
                return task.then((options) => {
                    this.json = options;
                });
            }
        },
    },

    computed: {
        isActive() {
            return Boolean(this.showList) && Boolean(this.json.length);
        },
        characterCountDisplay() {
            let message = '0';

            if (this.type) {
                message = `${this.type.length}`;
            }

            if (this.characterLimit) {
                message += `/${this.characterLimit}`;
            }

            return message;
        },
        overCharacterLimit() {
            return this.type && this.type.length > this.characterLimit;
        },
    },
    created() {
        // Sync parent model with initValue Props
        this.type = this.initValue ? this.initValue : null;
    },
    mounted() {
        if (this.required) this.$refs.input.setAttribute('required', this.required);
    },
};
</script>

<style lang="scss" scoped>
@import 'sass/app';

.autocomplete-wrapper {
    input.with-character-count {
        padding-right: 3.5em;
    }

    .character-count {
        color: #bbb;
        position: absolute;
        top: 0.4em;
        right: 0.5em;

        &.over-limit {
            color: #ec9ca6;
        }
    }

    .dropdown {
        display: block;
        min-width: 100%;
        position: relative;
    }

    .autocomplete-input {
        resize: none;
    }

    .resizable-textarea {
        resize: vertical !important;
    }

    .dropdown-content {
        display: block !important;
        overflow-y: auto;
        overflow-x: hidden;
        z-index: 50;
        min-width: 100%;

        .dropdown-item {
            text-decoration: none;
        }

        .dropdown-item.is-active {
            background-color: #88ab75;
            color: #fff;
        }
    }

    .dropdown-menu {
        width: auto;
        min-width: 100%;
        max-width: 200%;
        text-align: left;
    }

    //Bills Form Line Item Part Number-specific highlights
    textarea.highlight-pricelist-item {
        border-color: #d0b6d0;
        border-width: 4px;
        border-style: solid;
    }

    textarea.highlight-pdf-item {
        background-color: #88ab75;
        color: #fff;
    }

    textarea.highlight-non-pdf-item {
        background-color: #f6bd60;
    }
}

.highlight {
    overflow: visible;
}

.backdrop {
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    height: 100vh;
    width: 100vw;
    // Ensure the user can select a suggestion by clicking
    // by making the backdrop lower than the dropdown content.
    z-index: $dropdown-content-z - 1;
}
</style>
